/**
 * Registration Form Step - Extracted from RegistrationModal.
 * Handles the main form with user credentials, organization data, and address fields.
 * Uses snake_case keys matching backend payload (org_name, cnpj, razao_social, etc).
 */
import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Building2, Activity, Mail, Lock, Eye, EyeOff, User, ArrowRight, Loader2, KeyRound, FileText, Briefcase, Phone } from 'lucide-react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';

const formatCNPJ = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

const RegistrationFormStep = ({ form, setForm, isCompany, orgLabel, STEPS, setStep, setProfileType, handleFormNext, loading, cepLoading, setCepLoading }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleCepLookup = async (cep) => {
    const clean = cep.replace(/\D/g, '');
    setForm(f => ({ ...f, cep }));
    if (clean.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await axios.get(`${API}/cep/${clean}`);
      setForm(f => ({
        ...f,
        street: res.data.street || f.street,
        neighborhood: res.data.neighborhood || f.neighborhood,
        city: res.data.city || f.city,
        state: res.data.state || f.state,
      }));
      toast.success('CEP encontrado!');
    } catch (e) {
      console.error('CEP lookup failed:', e);
      toast.error('CEP nao encontrado');
    } finally {
      setCepLoading(false);
    }
  };

  const beforeNext = (e) => {
    e.preventDefault();
    if (confirmPassword !== form.password) {
      toast.error('As senhas não conferem');
      return;
    }
    handleFormNext(e);
  };

  return (
    <>
      <DialogHeader className="pt-2 pb-3">
        <DialogTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {isCompany ? <Building2 className="w-5 h-5 text-blue-600" /> : <Activity className="w-5 h-5 text-orange-500" />}
          Cadastro de {orgLabel}
        </DialogTitle>
        <DialogDescription>Preencha os dados para criar sua conta</DialogDescription>
      </DialogHeader>

      <form onSubmit={beforeNext} className="space-y-4">
        {/* User credentials section */}
        <div className="space-y-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <KeyRound className="w-3.5 h-3.5" /> Dados de Acesso
          </p>
          <div>
            <Label className="text-xs text-slate-600">Nome do responsável *</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Seu nome completo" required className="pl-10" data-testid="reg-name" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Cargo / Função</Label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={form.rh_name || ''} onChange={(e) => setForm({ ...form, rh_name: e.target.value })} placeholder={isCompany ? 'Ex: Gerente de RH' : 'Ex: Proprietário'} className="pl-10" data-testid="reg-position" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600">E-mail de acesso *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="seu@email.com" required className="pl-10" data-testid="reg-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Senha *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type={showPassword ? 'text' : 'password'} value={form.password || ''} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min. 6 caracteres" required minLength={6} className="pl-10 pr-10" data-testid="reg-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                  {showPassword ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Confirmar *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repita a senha" required className="pl-10 pr-10" data-testid="reg-confirm-password" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                  {showConfirmPassword ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Organization data section */}
        <div className="space-y-3 pt-2 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            {isCompany ? <Building2 className="w-3.5 h-3.5" /> : <Activity className="w-3.5 h-3.5" />} Dados da {orgLabel}
          </p>
          <div>
            <Label className="text-xs text-slate-600">Nome Fantasia da {orgLabel} *</Label>
            <Input value={form.org_name || ''} onChange={(e) => setForm({ ...form, org_name: e.target.value })} placeholder={`Nome como é conhecida no mercado`} required data-testid="reg-org-name" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">CNPJ {isCompany ? '*' : ''}</Label>
            <div className="relative">
              <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={form.cnpj || ''}
                onChange={(e) => setForm({ ...form, cnpj: formatCNPJ(e.target.value) })}
                placeholder="00.000.000/0000-00"
                required={isCompany}
                maxLength={18}
                className="pl-10"
                data-testid="reg-cnpj"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Razão Social</Label>
            <Input value={form.razao_social || ''} onChange={(e) => setForm({ ...form, razao_social: e.target.value })} placeholder="Nome jurídico completo (para NF)" data-testid="reg-razao-social" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Telefone comercial</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(00) 00000-0000" className="pl-10" data-testid="reg-phone" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">E-mail corporativo</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type="email" value={form.org_email || ''} onChange={(e) => setForm({ ...form, org_email: e.target.value })} placeholder="contato@empresa.com" className="pl-10" data-testid="reg-org-email" />
              </div>
            </div>
          </div>
        </div>

        {/* Address section */}
        <div className="space-y-3 pt-2 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Endereço</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <Label className="text-xs text-slate-600">CEP</Label>
              <div className="relative">
                <Input value={form.cep || ''} onChange={(e) => handleCepLookup(e.target.value)} placeholder="00000-000" maxLength={9} data-testid="reg-cep" />
                {cepLoading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />}
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-600">Rua</Label>
              <Input value={form.street || ''} onChange={(e) => setForm({ ...form, street: e.target.value })} placeholder="Rua, Avenida..." data-testid="reg-street" />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Número</Label>
              <Input value={form.number || ''} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="Nº" data-testid="reg-number" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Bairro</Label>
              <Input value={form.neighborhood || ''} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} data-testid="reg-neighborhood" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Cidade</Label>
              <Input value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="reg-city" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">UF</Label>
              <Input value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} maxLength={2} data-testid="reg-state" />
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 pt-2">
          <Button type="button" variant="outline" onClick={() => { setStep(STEPS.PROFILE); setProfileType(null); }} className="flex-1">
            Voltar
          </Button>
          <Button type="submit" disabled={loading} className={`flex-1 text-white ${isCompany ? 'bg-blue-600 hover:bg-blue-700' : 'bg-orange-500 hover:bg-orange-600'}`} data-testid="reg-next-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Avançar <ArrowRight className="w-4 h-4 ml-1" /></>}
          </Button>
        </div>
      </form>
    </>
  );
};

export default RegistrationFormStep;
